import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { EmailCampaign, EmailCampaignService } from '../services/emailCampaignService';

// Email Campaigns Hook Result Interface
interface UseEmailCampaignsResult {
  emailCampaigns: EmailCampaign[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createEmailCampaign: (campaignData: Omit<EmailCampaign, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateEmailCampaign: (campaignId: string, updates: Partial<Omit<EmailCampaign, 'id' | 'createdAt'>>) => Promise<void>;
  deleteEmailCampaign: (campaignId: string) => Promise<void>;
}

export const useEmailCampaigns = (): UseEmailCampaignsResult => {
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initial load and real-time subscription
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializeEmailCampaigns = async () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe to real-time updates
        unsubscribe = EmailCampaignService.subscribeToEmailCampaigns((campaigns) => {
          setEmailCampaigns(campaigns);
          setLoading(false);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load email campaigns';
        setError(errorMessage);
        setLoading(false);
        console.error('Error initializing email campaigns:', err);
      }
    };

    initializeEmailCampaigns();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Refresh function to manually reload email campaigns data
  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const campaigns = await EmailCampaignService.getEmailCampaigns();
      setEmailCampaigns(campaigns);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh email campaigns';
      setError(errorMessage);
      console.error('Error refreshing email campaigns:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create email campaign function
  const createEmailCampaign = async (campaignData: Omit<EmailCampaign, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const campaignId = await EmailCampaignService.createEmailCampaign(campaignData);
      return campaignId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create email campaign';
      setError(errorMessage);
      console.error('Error creating email campaign:', err);
      throw err;
    }
  };

  // Update email campaign function
  const updateEmailCampaign = async (campaignId: string, updates: Partial<Omit<EmailCampaign, 'id' | 'createdAt'>>): Promise<void> => {
    try {
      await EmailCampaignService.updateEmailCampaign(campaignId, updates);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update email campaign';
      setError(errorMessage);
      console.error('Error updating email campaign:', err);
      throw err;
    }
  };

  // Delete email campaign function
  const deleteEmailCampaign = async (campaignId: string): Promise<void> => {
    try {
      await EmailCampaignService.deleteEmailCampaign(campaignId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete email campaign';
      setError(errorMessage);
      console.error('Error deleting email campaign:', err);
      throw err;
    }
  };

  return {
    emailCampaigns,
    loading,
    error,
    refresh,
    createEmailCampaign,
    updateEmailCampaign,
    deleteEmailCampaign
  };
}; 